import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * TranscriptStatusDisplay Component
 * 
 * Displays the current status of transcript generation with elapsed time.
 * 
 * @param {Object} props
 * @param {string} props.status - Status: PENDING, PROCESSING, COMPLETED, FAILED
 * @param {string} props.startedAt - ISO timestamp when processing started
 * @param {string} props.completedAt - ISO timestamp when processing completed
 * @param {number} props.duration - Duration in seconds (for completed transcripts)
 * @param {string} props.errorMessage - Error message if failed
 * @param {string} props.provider - Provider name (e.g., 'whisper-1', 'sonix')
 * @param {boolean} props.compact - Compact display mode
 */
const TranscriptStatusDisplay = ({
  status,
  startedAt,
  completedAt,
  duration,
  errorMessage,
  provider,
  compact = false
}) => {
  const [elapsedTime, setElapsedTime] = useState(null);

  // Calculate elapsed time for PROCESSING status
  useEffect(() => {
    if (status !== 'PROCESSING' || !startedAt) {
      setElapsedTime(null);
      return;
    }

    const updateElapsed = () => {
      const now = new Date();
      const start = new Date(startedAt);
      const seconds = Math.floor((now - start) / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      if (minutes === 0) {
        setElapsedTime(`${remainingSeconds}sec`);
      } else if (remainingSeconds === 0) {
        setElapsedTime(`${minutes}min`);
      } else {
        setElapsedTime(`${minutes}min i ${remainingSeconds}sec`);
      }
    };

    // Update immediately
    updateElapsed();

    // Update every second
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [status, startedAt]);

  // Format duration for completed transcripts
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}sec`;
    if (secs === 0) return `${mins}min`;
    return `${mins}min ${secs}sec`;
  };

  // Calculate total duration for completed transcripts
  const getTotalDuration = () => {
    // If duration is provided, use it
    if (duration) {
      return duration;
    }
    
    // Otherwise, calculate from startedAt to completedAt
    if (status === 'COMPLETED' && startedAt && completedAt) {
      const start = new Date(startedAt);
      const end = new Date(completedAt);
      return Math.floor((end - start) / 1000);
    }
    
    return null;
  };

  // Render based on status
  const renderStatus = () => {
    switch (status) {
      case 'PENDING':
        return (
          <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
            <ClockIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} />
            <span className="text-gray-600">Waiting to start...</span>
          </div>
        );

      case 'PROCESSING':
        return (
          <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
            <ArrowPathIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-blue-500 animate-spin`} />
            <span className="text-blue-700">
              Processing{elapsedTime ? ` for ${elapsedTime}` : '...'}
            </span>
          </div>
        );

      case 'COMPLETED':
        const totalDuration = getTotalDuration();
        return (
          <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
            <CheckCircleIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-green-500`} />
            <span className="text-green-700">
              Completed{totalDuration ? ` in ${formatDuration(totalDuration)}` : ''}
            </span>
          </div>
        );

      case 'FAILED':
        return (
          <div className="space-y-2">
            <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
              <ExclamationCircleIcon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-red-500`} />
              <span className="text-red-700">
                Failed{elapsedTime ? ` after ${elapsedTime}` : ''}
              </span>
            </div>
            {errorMessage && !compact && (
              <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
                <strong>Error:</strong> {errorMessage.substring(0, 200)}
                {errorMessage.length > 200 && '...'}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className={`flex items-center gap-2 ${compact ? 'text-sm' : ''}`}>
            <span className="text-gray-500">No status available</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-2">
      {renderStatus()}
      {provider && !compact && (
        <div className="text-xs text-gray-500">
          Provider: <span className="font-medium">{provider}</span>
        </div>
      )}
    </div>
  );
};

export default TranscriptStatusDisplay;

